import React from 'react';
import { Link } from 'react-router-dom';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const faqs = [
  {
    category: 'Shipping',
    id: 'shipping',
    questions: [
      {
        q: 'How long does shipping take?',
        a: 'Standard shipping typically takes 5-7 business days within the US. Express shipping (2-3 business days) is available at checkout for an additional fee. International orders may take 10-14 business days.'
      },
      {
        q: 'Do you ship internationally?',
        a: 'Yes, we ship to most countries worldwide. International shipping rates and delivery times vary by destination. You can see the exact shipping cost at checkout.'
      },
      {
        q: 'How can I track my order?',
        a: 'Once your order ships, you\'ll receive a confirmation email with a tracking number. You can use this number to track your package on our website or the carrier\'s site.'
      }
    ]
  },
  {
    category: 'Returns & Exchanges',
    id: 'returns',
    questions: [
      {
        q: 'What is your return policy?',
        a: 'We offer a 30-day return policy for unworn items in original condition with tags attached. Items must be returned in their original packaging. Custom or personalized items cannot be returned.'
      },
      {
        q: 'How do I initiate a return?',
        a: 'Contact our customer service team at support@beltandtingz.com with your order number. We\'ll provide you with a prepaid return label and instructions for sending your item back.'
      },
      {
        q: 'When will I receive my refund?',
        a: 'Refunds are processed within 5-7 business days after we receive your returned item. The refund will be credited to your original payment method.'
      }
    ]
  },
  {
    category: 'Leather Care',
    id: 'care',
    questions: [
      {
        q: 'How do I care for my leather belt?',
        a: 'Store your belt rolled or hung to prevent creasing. Clean with a soft, dry cloth. For conditioning, use a quality leather conditioner every 3-6 months. Avoid exposure to water and direct sunlight.'
      },
      {
        q: 'My leather product got wet. What should I do?',
        a: 'Blot excess water with a soft cloth and let it dry naturally at room temperature. Avoid heat sources like hairdryers. Once dry, apply leather conditioner to restore moisture and prevent cracking.'
      },
      {
        q: 'Will my leather develop a patina?',
        a: 'Yes! Full-grain leather naturally develops a beautiful patina over time with regular use. This is a sign of quality leather and adds character to your piece.'
      }
    ]
  },
  {
    category: 'Products',
    id: 'products',
    questions: [
      {
        q: 'What type of leather do you use?',
        a: 'We use premium full-grain leather sourced from trusted tanneries. Full-grain leather is the highest quality leather available, known for its durability and beautiful aging characteristics.'
      },
      {
        q: 'How do I find my belt size?',
        a: 'Measure your current belt from the buckle to the hole you use most frequently. Our belts are sized by this measurement. If you\'re between sizes, we recommend sizing up.'
      },
      {
        q: 'Do you offer gift wrapping?',
        a: 'Yes! Select "Gift Wrap" at checkout for our premium packaging option. Each gift-wrapped item comes in a signature box with tissue paper and a ribbon.'
      }
    ]
  }
];

export default function FAQPage() {
  return (
    <div className="min-h-screen" data-testid="faq-page">
      {/* Hero */}
      <section className="bg-leather-900 px-6 md:px-12 lg:px-24 py-24 text-center">
        <h1 className="font-heading text-4xl md:text-5xl text-white mb-4">FAQ</h1>
        <p className="text-cream-100/70 max-w-lg mx-auto">
          Find answers to commonly asked questions about our products and policies.
        </p>
      </section>

      {/* FAQ Sections */}
      <section className="px-6 md:px-12 lg:px-24 py-24">
        <div className="max-w-3xl mx-auto">
          {faqs.map((section) => (
            <div key={section.id} id={section.id} className="mb-12 scroll-mt-24">
              <h2 className="font-heading text-2xl text-leather-900 mb-6">{section.category}</h2>
              <Accordion type="single" collapsible className="space-y-4">
                {section.questions.map((faq, index) => (
                  <AccordionItem
                    key={index}
                    value={`${section.id}-${index}`}
                    className="border border-cream-200 px-6"
                  >
                    <AccordionTrigger className="text-left text-leather-900 hover:no-underline py-4">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-leather-600 pb-4">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="px-6 md:px-12 lg:px-24 py-16 bg-cream-100 text-center">
        <h2 className="font-heading text-2xl text-leather-900 mb-4">Still Have Questions?</h2>
        <p className="text-leather-600 mb-6">
          Our customer service team is here to help.
        </p>
        <Link to="/contact" className="btn-primary">
          Contact Us
        </Link>
      </section>
    </div>
  );
}
